import React from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useEffectOnce, usePathParam, usePagination } from '@chilecompra/react-kit'
import { Pagination, Table, TableRow, Toast, Typography } from '@chilecompra/react-kit/components'
import { onGetCompanyContactInformationThunk } from './CompanyContactSection.actions'
import ProtectByProfile from '../../components/Security/ProtectByProfile'
import TablePlaceholder from '../../components/Placeholders/TablePlaceholder'
import { Content, Separator, TableCell } from './CompanyContactSection.styles'
import { useAuthorization } from '../AuthorizationProvider/AuthorizationProvider.hooks'

/**
 * The Company Contact section container.
 */
const CompanyContactSection = () => {
  const dispatch = useDispatch()
  const { userType } = useAuthorization()
  const dni = usePathParam('rut')
  const rowsPerPage = 5
  const { contacts, error, loading } = useSelector(state => state.companyContactInformation)

  const { page, totalPages, onChangePage, onGetCurrentPage } = usePagination({
    data: contacts,
    initialPage: 1,
    rowsPerPage
  })

  useEffectOnce(() => {
    if (!loading) {
      return
    }

    dispatch(onGetCompanyContactInformationThunk({ dni }))
  })

  return error && error?.reason !== 'SOMETHING_WENT_WRONG_ERROR' ? null : (
    <>
      <Content>
        <Typography fontWeight="bold" padding={userType === 'Comprador' ? '0 0 16px' : undefined} variant="h4">
          Usuarios de la empresa
        </Typography>

        <ProtectByProfile rule={userType => userType === 'Proveedor'}>
          <Typography margin="4px 0 8px" variant="subtitle">
            Para editar esta información, dirígete a la sección Usuarios del menú Administración - Mi empresa en tu
            escritorio de Mercado Público.
          </Typography>
        </ProtectByProfile>

        {loading && <TablePlaceholder margin="9px 0 0" />}

        {!error && !loading && contacts?.length !== 0 && (
          <Table accessibility={{ label: 'Contacto de la empresa' }}>
            <TableRow>
              <TableCell disableBottomBorder enableHead>
                Nombre
              </TableCell>
              <TableCell disableBottomBorder enableHead>
                Cargo
              </TableCell>
              <TableCell disableBottomBorder enableHead>
                Correo laboral
              </TableCell>
              <TableCell disableBottomBorder enableHead>
                Teléfono Laboral
              </TableCell>
            </TableRow>

            {onGetCurrentPage().map((contact, index) => {
              return (
                <TableRow key={`contact-${index}`}>
                  <TableCell disableBottomBorder>
                    <Typography variant="body2">{contact.name}</Typography>
                    <Typography variant="body2" color="black3">
                      {`Contacto ${contact.contactType.join(' y ')}`}
                    </Typography>
                  </TableCell>
                  <TableCell disableBottomBorder>
                    <Typography variant="body2">{contact.position}</Typography>
                    <Typography variant="body2" color="black3">
                      {contact.salesUnit}
                    </Typography>
                  </TableCell>
                  <TableCell disableBottomBorder>
                    <Typography variant="body2">{contact.email}</Typography>
                  </TableCell>
                  <TableCell disableBottomBorder>
                    <Typography variant="body2">{contact.phone}</Typography>
                  </TableCell>
                </TableRow>
              )
            })}
          </Table>
        )}

        {!error && !loading && contacts?.length > rowsPerPage && (
          <Pagination alignment="left" count={totalPages} page={page} onChange={onChangePage} />
        )}

        {!error && !loading && contacts?.length === 0 && (
          <Typography color="black3" variant="body2">
            Este proveedor no ha ingresado contactos para la empresa.
          </Typography>
        )}

        {!loading && error && error.reason === 'SOMETHING_WENT_WRONG_ERROR' && (
          <Toast
            border
            severity="error"
            textDirection="vertical"
            title="Lo sentimos, estamos presentando problemas con nuestro servidor"
          >
            En estos momentos tenemos problemas para disponibilizar la información. Por favor, consulta en unos minutos
            más.
          </Toast>
        )}
      </Content>

      <Separator />
    </>
  )
}

export default CompanyContactSection
